"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="25168" className="hidden md:flex gap-8">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-5 w-24 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative px-4 py-2 text-sm font-semibold tracking-tight text-foreground/70 transition-colors hover:text-primary">
                {item.label}
                <span className="absolute bottom-0 left-0 h-[2px] w-full origin-left scale-x-0 bg-primary transition-transform duration-300 ease-out group-hover:scale-x-100" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-8 w-full bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-2 p-4">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="block rounded-lg px-4 py-3 text-base font-semibold text-foreground hover:bg-accent hover:text-accent-foreground transition-colors" onClick={onClose}>
          {item.label}
        </Link>)}
      <div className="mt-4 flex flex-col gap-3 px-4">
        <Button variant="ghost" className="w-full justify-start px-0 text-foreground/70" asChild>
          <Link to="#">Login</Link>
        </Button>
        <Button className="w-full rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 shadow-md" asChild>
          <Link to="#">Book a Discovery Call</Link>
        </Button>
      </div>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="main-navigation" className={cn("sticky top-0 z-50 w-full transition-all duration-300 ease-in-out border-b", isScrolled ? "bg-transparent backdrop-blur-md border-border/50 shadow-sm" : "bg-[#F2F6F8] border-transparent")}>
      <div className="container mx-auto flex h-20 items-center justify-between px-4 !text-[hsl(200,30%,96%)] md:px-6">
        {}
        <div className="flex items-center gap-2">
          <Link to="/HomePage" className="flex items-center gap-2 outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md">
            <img src="https://dashing-rooster-1.10web.cloud/wp-content/uploads/2026/03/compass-icon-transparent.dim_200x200.png" alt="FinTax Logo" className="h-8 w-auto object-contain" />
            <h1 className="font-serif text-[20px] text-[#061D2B] font-bold">Northland Financial Solutions</h1>
          </Link>
        </div>

        {}
        <div className="hidden md:block">
          <MenuProvider menu_id="2" className="w-full">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden items-center gap-6 md:flex">
          <Button variant="ghost" className="text-sm font-semibold text-foreground/70 hover:text-foreground transition-colors" asChild>
            
          </Button>
          <Button className="rounded-lg bg-primary px-8 font-semibold text-primary-foreground shadow-md hover:bg-[#1E323F] transition-all hover:-translate-y-0.5 hover:shadow-lg" asChild>
            <Link to="https://calendly.com/taxlegacycompass" className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive h-9 py-2 has-[>svg]:px-3 rounded-lg px-8 font-semibold text-primary-foreground shadow-md transition-all !bg-[hsl(203,76%,10%)] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-[#1E323F] hover:-translate-y-0.5 hover:shadow-lg">Book a Discovery Call</Link>
          </Button>
        </div>

        {}
        <Button variant="ghost" size="icon" className="md:hidden text-foreground" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
          {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
        </Button>
      </div>

      {}
      {isMobileMenuOpen && <div className="absolute left-0 top-20 z-40 w-full border-b border-border bg-background shadow-xl md:hidden animate-in slide-in-from-top-2 fade-in duration-200">
          <MenuProvider menu_id="2" className="w-full">
            <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
        </div>}
    </header>;
}